import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { FileSignature, Loader2, Upload, Download, PenLine, CheckCircle2 } from "lucide-react";
import ESignDialog from "@/components/ESignDialog";
import { toast } from "sonner";

const C_COLOR = { sent: "#F5A623", signed: "#10B981", void: "#71717A", draft: "#71717A" };

export default function AdminContracts() {
  const fileRef = useRef();
  const [contracts, setContracts] = useState(null);
  const [signing, setSigning] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ organization_id: "", contract_type: "engaged_search", file: null });

  const load = () => api.get("/contracts").then(({ data }) => setContracts(data));
  useEffect(() => { load(); api.get("/organizations").then(({ data }) => setOrgs(data)); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!f.organization_id || !f.file) return toast.error("Organization and file required");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("organization_id", f.organization_id);
      fd.append("contract_type", f.contract_type);
      fd.append("file", f.file);
      await api.post("/contracts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Contract uploaded");
      setOpen(false); setF({ organization_id: "", contract_type: "engaged_search", file: null }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    const fd = new FormData();
    fd.append("status", status);
    await api.patch(`/contracts/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(`Marked ${status}`);
    load();
  };

  return (
    <div>
      <PageHeader overline="Agreements & e-sign" title="Contracts">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-contract-btn" className="rounded-none bg-primary hover:bg-[#09090B]"><Upload className="h-4 w-4 mr-1" /> Upload</Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-black/10 rounded-none">
            <DialogHeader><DialogTitle className="font-heading">Upload Contract</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-2">
                <Label>Organization</Label>
                <Select value={f.organization_id} onValueChange={(v) => setF({ ...f, organization_id: v })}>
                  <SelectTrigger data-testid="contract-org" className="bg-[#F4F4F5] border-black/10 rounded-none"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white border-black/10">{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={f.contract_type} onValueChange={(v) => setF({ ...f, contract_type: v })}>
                  <SelectTrigger className="bg-[#F4F4F5] border-black/10 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-black/10">
                    {["engaged_search", "placement_fee", "msa", "job_posting"].map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>File (PDF/DOC)</Label>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={(e) => setF({ ...f, file: e.target.files?.[0] })} data-testid="contract-file"
                  className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:border file:border-black/10 file:bg-[#F4F4F5] file:text-sm" />
              </div>
              <DialogFooter><Button type="submit" data-testid="contract-save" disabled={saving} className="rounded-none bg-primary hover:bg-[#09090B]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="p-8">
        {!contracts && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {contracts && contracts.length === 0 && <Empty icon={FileSignature} title="No contracts yet" hint="Upload an engaged-search agreement or MSA and track its signature status." />}
        {contracts && contracts.length > 0 && (
          <div className="space-y-px bg-black/10 border border-black/10">
            {contracts.map((c) => (
              <div key={c.id} className="bg-white p-5 flex items-center justify-between" data-testid={`contract-${c.id}`}>
                <div>
                  <p className="font-medium">{c.organization_name}</p>
                  <p className="overline text-[0.55rem] mt-1 capitalize">{(c.contract_type || "").replace(/_/g, " ")} · {c.filename}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1.5 text-xs capitalize"><span className="h-1.5 w-1.5 rounded-full" style={{ background: C_COLOR[c.status] || "#71717A" }} />{c.status}{c.signer_name ? ` · ${c.signer_name}` : ""}</span>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${c.file_url}`} target="_blank" rel="noreferrer" className="p-2 border border-black/10 hover:bg-black/5 transition-colors" title="Download"><Download className="h-4 w-4" /></a>
                  {c.status === "signed" ? (
                    <span className="flex items-center gap-1 text-xs text-[#10B981]"><CheckCircle2 className="h-4 w-4" /> Signed</span>
                  ) : (
                    <Button size="sm" onClick={() => setSigning(c)} data-testid={`sign-${c.id}`} className="rounded-none bg-primary hover:bg-[#09090B]"><PenLine className="h-4 w-4 mr-1" /> E-sign</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
